import React, { useEffect, useState } from "react";

import { InfoSmIcon, Typography } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";

import { IconControl } from "ds/Button";
import Dialog from "ds/Dialog";

import * as S from "./MonthlyTraffic.styles";

import type {
  OrganizationSubscription,
  Subscription,
  SubscriptionCurrentUsage
} from "@packages/client";

export const MonthlyTraffic = ({
  subscription
}: {
  subscription?: Subscription | OrganizationSubscription;
}) => {
  const intl = useIntl();
  const [currentUsage, setCurrentUsage] = useState<SubscriptionCurrentUsage>();

  const dateRange = `${moment().startOf("month").format("MMM D")} - ${moment()
    .endOf("month")
    .format("MMM D, YYYY")}`;

  useEffect(() => {
    const getCurrentUsage = async () => {
      if (!subscription?.getCurrentUsage) return;

      const current = await subscription.getCurrentUsage({
        includeNotCharged: true
      });

      setCurrentUsage(current);
    };

    getCurrentUsage();
  }, [subscription]);

  const hasFastly =
    ((subscription &&
      "fastly_service_ids" in subscription &&
      subscription.fastly_service_ids?.length) ||
      0) > 0;

  if (!currentUsage) return null;

  return (
    <S.Details>
      <S.TitleWrapper>
        <Typography.Title tag="h3">
          {intl.formatMessage({ id: "project.details.traffic_this_month" })}
        </Typography.Title>
        <Dialog
          description={intl.formatMessage({
            id: "project.details.traffic_note"
          })}
          trigger={
            <IconControl aria-label="traffic">
              <InfoSmIcon />
            </IconControl>
          }
        />
      </S.TitleWrapper>

      <S.DateRange size="small">
        {intl.formatMessage(
          {
            id: "project.details.billing_period"
          },
          {
            value: dateRange
          }
        )}
      </S.DateRange>

      <S.Infos>
        {currentUsage.egress_bandwidth && (
          <S.Info>
            <S.Value>
              {currentUsage.egress_bandwidth.current_usage_formatted}
            </S.Value>
            <S.Label>
              {intl.formatMessage({
                id: "project.details.origin_bandwidth"
              })}
            </S.Label>
          </S.Info>
        )}

        {currentUsage.ingress_requests && (
          <S.Info>
            <S.Value>
              {currentUsage.ingress_requests.current_usage_formatted.replace(
                " requests",
                ""
              )}
            </S.Value>
            <S.Label>
              {intl.formatMessage({
                id: "project.details.origin_requests"
              })}
            </S.Label>
          </S.Info>
        )}

        {hasFastly && currentUsage.fastly_bandwidth && (
          <S.Info>
            <S.Value>
              {currentUsage.fastly_bandwidth.current_usage_formatted}
            </S.Value>
            <S.Label>
              {intl.formatMessage({
                id: "project.details.cdn_bandwidth"
              })}
            </S.Label>
          </S.Info>
        )}

        {hasFastly && currentUsage.fastly_requests && (
          <S.Info>
            <S.Value>
              {currentUsage.fastly_requests.current_usage_formatted.replace(
                " requests",
                ""
              )}
            </S.Value>
            <S.Label>
              {intl.formatMessage({
                id: "project.details.cdn_requests"
              })}
            </S.Label>
          </S.Info>
        )}
      </S.Infos>
    </S.Details>
  );
};
